// `tutorialLifecycle.ts` wired to the app: a run's outcome written to
// `user.tutorial`, the offer card's «Позже», and «Удалить учебный проект».

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDeleteProject } from '@/api/mutations/useProjectMutations';
import { useUpdateUserTutorial } from '@/api/mutations/useUserTutorialMutations';
import { useUserQuery } from '@/api/queries/useAuth';
import { paths } from '@/constants';
import { userTutorialEntity } from '@/data/entities/userTutorial';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import type { RunOutcome, TutorialUnit } from '@/types';
import { chaptersAfterRun, coreAfterRun, deleteTutorialProject } from './tutorialLifecycle';
import { offerAfterLater } from './tutorialOffer';

export function useTutorialLifecycle() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const { data: user } = useUserQuery();
    const userId = user?.id;
    const { mutateAsync: updateTutorial } = useUpdateUserTutorial();
    const { mutateAsync: deleteProject } = useDeleteProject();

    /** Records how a run ended — unless that changes nothing (a replay, or
     *  «Выйти» from a chapter). */
    const recordRun = useCallback(
        async (unit: TutorialUnit, outcome: RunOutcome) => {
            if (userId === undefined) return;
            try {
                // Past the cache, like «Начать»: the decision must not run on a stale state.
                const { core, chapters } = await userTutorialEntity.fetchers.get.fetch({ userId });
                if (unit === 'core') {
                    const next = coreAfterRun(core, outcome);
                    if (next !== core) await updateTutorial({ userId, patch: { core: next } });
                    return;
                }
                const next = chaptersAfterRun(chapters, unit, outcome);
                if (next !== chapters) await updateTutorial({ userId, patch: { chapters: [...next] } });
            } catch (error) {
                // biome-ignore lint/suspicious/noConsole: the run still ends; only the record is lost
                console.error('[tutorial] could not record the run:', error);
            }
        },
        [userId, updateTutorial],
    );

    /** The offer card's «Позже»: snoozed to the next day, the third time «Не показывать». */
    const snoozeOffer = useCallback(async () => {
        if (userId === undefined) return;
        try {
            const { offer } = await userTutorialEntity.fetchers.get.fetch({ userId });
            await updateTutorial({ userId, patch: offerAfterLater(offer, new Date()) });
        } catch (error) {
            // biome-ignore lint/suspicious/noConsole: the card is gone for now; only the snooze is lost
            console.error('[tutorial] could not snooze the offer:', error);
        }
    }, [userId, updateTutorial]);

    /** Deletes the tutorial project. From inside it (`leave`), the hub comes
     *  first, so the editor never refetches a project in the trash. */
    const removeProject = useCallback(
        async (projectId: number, { leave = true }: { leave?: boolean } = {}) => {
            if (userId === undefined) return;
            if (leave) navigate(paths.tutorial.index);
            try {
                await deleteTutorialProject(projectId, {
                    trashProject: async id => {
                        await deleteProject({ project_id: id });
                    },
                    readTutorial: () => userTutorialEntity.fetchers.get.fetch({ userId }),
                    clearProjectId: async () => {
                        await updateTutorial({ userId, patch: { project_id: null } });
                    },
                });
            } catch (error) {
                // biome-ignore lint/suspicious/noConsole: the snackbar says it failed, the console says why
                console.error('[tutorial] delete failed:', error);
                showSnackbar({ open: true, message: t('tutorial.end.error-delete'), severity: snackbarColor.error });
            }
        },
        [userId, navigate, deleteProject, updateTutorial, showSnackbar, t],
    );

    return { recordRun, snoozeOffer, removeProject };
}
